import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { AgentActivityAtom } from '../../pages/ChatBot/hooks/agentActivityAtom';
import {
  ChatConversationIdAtom,
  ChatMessagesAtom,
  RequestedConversationIdAtom,
  SeedChatPromptAtom,
  useCreateNewChat,
} from '../../pages/ChatBot/hooks/chatActionHooks';
import { ShowChatBotAtom } from './chatbot-state';

export interface OpenAssistantOptions {
  /** Sent as the opening message once the panel is up, into the conversation shown or a new one. */
  prompt?: string;
  /** The conversation to show: the one shown now (default), a fresh one, or one by id, whose
   *  history the panel loads once it is up. */
  conversation?: 'current' | 'new' | number;
  /** Send the prompt only into an empty conversation; an ongoing chat is never hijacked. */
  onlyIfEmpty?: boolean;
}

/**
 * The one way a page opens or closes the assistant. Built over the panel's own atoms and
 * nothing else, so any page can call it; the panel does the loading once it is up.
 */
export function useAssistant() {
  const createNewChat = useCreateNewChat();

  const openAssistant = useAtomCallback(
    useCallback(
      (get, set, { prompt, conversation = 'current', onlyIfEmpty = false }: OpenAssistantOptions = {}) => {
        if (conversation === 'new') {
          createNewChat({ closeMenu: true });
        } else if (typeof conversation === 'number') {
          if (get(ChatConversationIdAtom) !== conversation) {
            // Shown at once, so the panel header and the attention badge follow; the panel loads
            // the history when it mounts.
            set(ChatConversationIdAtom, conversation);
            set(ChatMessagesAtom, []);
            set(AgentActivityAtom, {});
            set(RequestedConversationIdAtom, conversation);
          } else if (!get(ShowChatBotAtom)) {
            // Closed on this very conversation, the panel missed its frames: it reloads the
            // history, over what it last showed.
            set(RequestedConversationIdAtom, conversation);
          }
        }
        set(ShowChatBotAtom, true);
        if (prompt) {
          set(SeedChatPromptAtom, { prompt, onlyIfEmpty });
        }
      },
      [createNewChat]
    )
  );

  const closeAssistant = useAtomCallback(
    useCallback((_get, set) => {
      set(ShowChatBotAtom, false);
    }, [])
  );

  return { openAssistant, closeAssistant };
}
